import axios from 'axios';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import {
  MessageSquare, Plus, Trash2, Home, TreePine, Save,
  Edit3, Check, X, Minus, ChevronDown, ChevronUp,
  Layers, AlertCircle, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

// ─── Questions par défaut ─────────────────────────────────────────────────────

const QUESTIONS_DEFAUT_INTERIEUR = [
  "Les murs sont-ils en bon état ? (fissures, trous, dommages)",
  "Y a-t-il des plafonds à peindre ?",
  "Des portes intérieures à peindre ?",
  "Des boiseries / moulures à inclure ?",
  "Y a-t-il des armoires à peindre ?",
  "Escalier intérieur à inclure ?",
  "Sous-sol à inclure dans les travaux ?",
  "Garage à inclure ?",
  "Y a-t-il des réparations de surfaces requises ?",
  "Le client sera-t-il présent lors des travaux ?",
  "Travaux à réaliser en plusieurs phases ?",
  "Contraintes d'accès particulières ?",
];

const QUESTIONS_DEFAUT_EXTERIEUR = [
  "La façade est-elle en bon état général ?",
  "Y a-t-il du bois pourri ou à remplacer ?",
  "Galerie / balcon à inclure ?",
  "Soffite / fascia à peindre ?",
  "Escaliers extérieurs à inclure ?",
  "Garde-corps extérieur à peindre ?",
  "Cabanon / remise inclus ?",
  "Clôture à inclure ?",
  "Accès à l'électricité disponible sur place ?",
  "Permis municipal requis ?",
  "Peinture sur brique ou béton ?",
  "Travaux de calfeutrage requis ?",
];

function buildQuestions(labels) {
  return labels.map((q, i) => ({
    id: `q_${i}_${Date.now()}`,
    question: q,
    reponse: null,   // 'oui' | 'non' | 'na' | null
    commentaire: '',
  }));
}

function initQuestions(saved, defaults, globalCustom = []) {
  if (saved && saved.length > 0) return saved;
  // Combiner les questions par défaut + les questions globales personnalisées
  const allLabels = [...defaults, ...globalCustom];
  return buildQuestions(allLabels);
}

// ─── Composant bouton Oui / Non / N/A ────────────────────────────────────────

function ReponseButtons({ value, onChange, questionId }) {
  const opts = [
    { key: 'oui', label: 'Oui', activeClass: 'bg-green-500 text-white border-green-500 shadow-sm', inactiveClass: 'bg-white text-gray-500 border-gray-200 hover:border-green-400 hover:text-green-600' },
    { key: 'non', label: 'Non', activeClass: 'bg-red-500 text-white border-red-500 shadow-sm', inactiveClass: 'bg-white text-gray-500 border-gray-200 hover:border-red-400 hover:text-red-600' },
    { key: 'na', label: 'N/A', activeClass: 'bg-gray-400 text-white border-gray-400 shadow-sm', inactiveClass: 'bg-white text-gray-400 border-gray-200 hover:border-gray-400 hover:text-gray-600' },
  ];
  return (
    <div className="flex gap-1" role="group" aria-label="Réponse">
      {opts.map(({ key, label, activeClass, inactiveClass }) => (
        <button
          key={key}
          type="button"
          data-testid={`reponse-${key}-${questionId}`}
          onClick={() => onChange(value === key ? null : key)}
          className={`px-3 py-1 text-xs font-semibold rounded-md border transition-all ${value === key ? activeClass : inactiveClass}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

// ─── Ligne de question ────────────────────────────────────────────────────────

function QuestionRow({ item, onReponse, onCommentaire, onDelete, onEditLabel, index }) {
  const [showComment, setShowComment] = useState(!!item.commentaire);
  const [editingLabel, setEditingLabel] = useState(false);
  const [labelDraft, setLabelDraft] = useState(item.question);

  const repondedClass = item.reponse === 'oui'
    ? 'border-green-200 bg-green-50'
    : item.reponse === 'non'
    ? 'border-red-200 bg-red-50'
    : item.reponse === 'na'
    ? 'border-gray-200 bg-gray-50'
    : 'border-gray-200 bg-white';

  const handleLabelSave = () => {
    if (labelDraft.trim()) onEditLabel(labelDraft.trim());
    setEditingLabel(false);
  };

  return (
    <div className={`border rounded-xl p-3 transition-all ${repondedClass}`}>
      {/* Ligne principale */}
      <div className="flex items-start gap-3">
        {/* Numéro */}
        <span className="w-5 h-5 mt-0.5 flex-shrink-0 text-xs font-bold text-gray-400 text-center">{index + 1}.</span>

        {/* Question (éditable en double-clic) */}
        <div className="flex-1 min-w-0">
          {editingLabel ? (
            <Input
              value={labelDraft}
              onChange={e => setLabelDraft(e.target.value)}
              onBlur={handleLabelSave}
              onKeyDown={e => { if (e.key === 'Enter') handleLabelSave(); if (e.key === 'Escape') setEditingLabel(false); }}
              className="text-sm h-7 py-0"
              autoFocus
            />
          ) : (
            <p
              className={`text-sm leading-snug cursor-text select-none group/q flex items-center gap-1 ${item.reponse ? 'font-medium' : 'text-gray-700'}`}
              onDoubleClick={() => setEditingLabel(true)}
              title="Double-cliquez pour modifier la question"
            >
              <span>{item.question}</span>
              <Edit3 className="w-3 h-3 text-gray-300 opacity-0 group-hover/q:opacity-100 transition-opacity flex-shrink-0" />
            </p>
          )}
        </div>

        {/* Réponse + actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <ReponseButtons value={item.reponse} onChange={onReponse} questionId={item.id} />
          <button
            type="button"
            title="Ajouter un commentaire"
            onClick={() => setShowComment(v => !v)}
            className={`p-1 rounded hover:bg-gray-100 transition-colors ${item.commentaire ? 'text-blue-500' : 'text-gray-300 hover:text-gray-500'}`}
          >
            <Edit3 className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            title="Supprimer cette question"
            onClick={onDelete}
            className="p-1 rounded hover:bg-red-50 text-gray-300 hover:text-red-400 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Commentaire */}
      {(showComment || item.commentaire) && (
        <div className="mt-2 ml-8">
          <Textarea
            value={item.commentaire}
            onChange={e => onCommentaire(e.target.value)}
            placeholder="Commentaire ou précision..."
            rows={2}
            className="text-xs resize-none"
          />
        </div>
      )}
    </div>
  );
}

// ─── Ajout de questions "en rafale" ──────────────────────────────────────────

function AjoutRafale({ onAdd, onClose }) {
  const [lines, setLines] = useState(['']);
  const refs = useRef([]);

  const updateLine = (i, val) => {
    setLines(prev => { const n = [...prev]; n[i] = val; return n; });
  };

  const handleKeyDown = (e, i) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (lines[i].trim()) {
        // Ajouter une nouvelle ligne et focus dessus
        setLines(prev => {
          const n = [...prev];
          n.splice(i + 1, 0, '');
          return n;
        });
        setTimeout(() => refs.current[i + 1]?.focus(), 50);
      }
    }
    if (e.key === 'Backspace' && lines[i] === '' && lines.length > 1) {
      e.preventDefault();
      setLines(prev => { const n = [...prev]; n.splice(i, 1); return n; });
      setTimeout(() => refs.current[Math.max(0, i - 1)]?.focus(), 50);
    }
  };

  const handleConfirm = () => {
    const valides = lines.filter(l => l.trim());
    if (valides.length === 0) { onClose(); return; }
    onAdd(valides);
    onClose();
  };

  return (
    <div className="border-2 border-dashed border-blue-300 rounded-xl p-4 bg-blue-50 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-blue-800">
          Ajout rapide — tapez vos questions, appuyez sur <kbd className="bg-blue-200 text-blue-800 px-1 rounded text-xs">Entrée</kbd> pour passer à la suivante
        </p>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
      </div>
      <div className="space-y-2">
        {lines.map((line, i) => (
          <div key={line.id || line._id || i} className="flex items-center gap-2">
            <span className="text-xs text-gray-400 w-4 text-right">{i + 1}.</span>
            <Input
              ref={el => refs.current[i] = el}
              value={line}
              onChange={e => updateLine(i, e.target.value)}
              onKeyDown={e => handleKeyDown(e, i)}
              placeholder={i === 0 ? "Tapez une question, puis Entrée pour en ajouter une autre..." : "Question suivante..."}
              className="text-sm h-8 bg-white"
              autoFocus={i === lines.length - 1}
            />
          </div>
        ))}
      </div>
      <div className="flex gap-2 justify-end pt-1">
        <Button size="sm" variant="ghost" onClick={onClose}>Annuler</Button>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={handleConfirm}>
          <Check className="w-3.5 h-3.5 mr-1" />
          Ajouter {lines.filter(l => l.trim()).length} question{lines.filter(l => l.trim()).length > 1 ? 's' : ''}
        </Button>
      </div>
    </div>
  );
}

// ─── Bloc questionnaire (intérieur OU extérieur) ──────────────────────────────

function BlocQuestionnaire({ type, icon: Icon, title, color, questions, onChange }) {
  const [showRafale, setShowRafale] = useState(false);

  const nb_repondues = questions.filter(q => q.reponse !== null).length;
  const nb_total = questions.length;

  const updateQ = (id, field, value) => {
    onChange(questions.map(q => q.id === id ? { ...q, [field]: value } : q));
  };

  const deleteQ = (id) => onChange(questions.filter(q => q.id !== id));

  const addRafale = async (nouvelles) => {
    const newItems = nouvelles.map((q, i) => ({
      id: `custom_${type}_${Date.now()}_${i}`,
      question: q,
      reponse: null,
      commentaire: '',
    }));
    onChange([...questions, ...newItems]);

    // Sauvegarder les nouvelles questions globalement pour les futurs dossiers
    try {
      const res = await axios.get(`${API_URL}/api/crm/questions-globales`);
      if (res.status === 200) {
        const current = res.data;
        const field = type === 'interieur' ? 'interieur' : 'exterieur';
        const existing = current[field] || [];
        const updated = [...existing, ...nouvelles];
        await axios.put(`${API_URL}/api/crm/questions-globales`, { ...current, [field]: updated });
        toast.success('Questions ajoutées et sauvegardées pour les futurs dossiers');
      }
    } catch {
      // Silencieux — les questions sont ajoutées localement même si la sauvegarde globale échoue
    }
  };

  const progressPct = nb_total > 0 ? Math.round(nb_repondues / nb_total * 100) : 0;

  return (
    <div className="space-y-3">
      {/* En-tête progress */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-600 font-medium">{nb_repondues}/{nb_total} questions répondues</span>
          {nb_repondues === nb_total && nb_total > 0 && (
            <Badge className="bg-green-100 text-green-700 text-xs">Complet</Badge>
          )}
        </div>
        <div className="flex items-center gap-1 text-xs text-gray-400">
          <span className="text-green-600 font-semibold">{questions.filter(q => q.reponse === 'oui').length} Oui</span>
          <span className="mx-1">·</span>
          <span className="text-red-500 font-semibold">{questions.filter(q => q.reponse === 'non').length} Non</span>
          <span className="mx-1">·</span>
          <span className="text-gray-400 font-semibold">{questions.filter(q => q.reponse === 'na').length} N/A</span>
        </div>
      </div>

      {/* Barre de progression */}
      <div className="w-full bg-gray-100 rounded-full h-1.5">
        <div
          className={`h-1.5 rounded-full transition-all ${progressPct === 100 ? 'bg-green-500' : 'bg-blue-500'}`}
          style={{ width: `${progressPct}%` }}
        />
      </div>

      {/* Liste de questions */}
      <div className="space-y-2">
        {questions.map((item, index) => (
          <QuestionRow
            key={item.id}
            item={item}
            index={index}
            onReponse={(val) => updateQ(item.id, 'reponse', val)}
            onCommentaire={(val) => updateQ(item.id, 'commentaire', val)}
            onDelete={() => deleteQ(item.id)}
            onEditLabel={(val) => updateQ(item.id, 'question', val)}
          />
        ))}
      </div>

      {/* Ajout en rafale */}
      {showRafale ? (
        <AjoutRafale onAdd={addRafale} onClose={() => setShowRafale(false)} />
      ) : (
        <Button
          variant="ghost"
          size="sm"
          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 w-full border border-dashed border-blue-200"
          onClick={() => setShowRafale(true)}
          data-testid={`ajout-rafale-${type}`}
        >
          <Plus className="w-3.5 h-3.5 mr-1" />
          Ajouter des questions
        </Button>
      )}
    </div>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────

const TYPE_COLORS = {
  generale: 'bg-gray-100 text-gray-800',
  suivi: 'bg-blue-100 text-blue-800',
  rappel: 'bg-yellow-100 text-yellow-800',
  importante: 'bg-red-100 text-red-800',
};

export function OngletNotes({ dossier, onUpdate }) {
  // ── États questionnaire
  const [typesTravaux, setTypesTravaux] = useState(dossier.questionnaire_type || null);
  const [questionsInt, setQuestionsInt] = useState([]);
  const [questionsExt, setQuestionsExt] = useState([]);
  const [notesLibres, setNotesLibres] = useState(dossier.notes_inspection || '');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [globalQLoaded, setGlobalQLoaded] = useState(false);

  // ── États notes texte
  const [nouvelleNote, setNouvelleNote] = useState('');
  const [typeNote, setTypeNote] = useState('generale');
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(true);

  // Charger les questions globales + initialiser
  useEffect(() => {
    const init = async () => {
      let globalInt = [];
      let globalExt = [];
      try {
        const res = await axios.get(`${API_URL}/api/crm/questions-globales`);
        if (res.status === 200) {
          const data = res.data;
          globalInt = data.interieur || [];
          globalExt = data.exterieur || [];
        }
      } catch (e) { console.error("Error:", e); }
      setQuestionsInt(initQuestions(dossier.questions_interieur, QUESTIONS_DEFAUT_INTERIEUR, globalInt));
      setQuestionsExt(initQuestions(dossier.questions_exterieur, QUESTIONS_DEFAUT_EXTERIEUR, globalExt));
      setGlobalQLoaded(true);
    };
    init();
  }, [dossier.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadNotes(); }, [dossier.id]);

  // Ref pour éviter les stale closures dans le timer d'auto-save
  const autoSaveTimer = useRef(null);
  const saveRef = useRef(null);

  const wrapQInt = (val) => { setQuestionsInt(val); setDirty(true); scheduleAutoSave(); };
  const wrapQExt = (val) => { setQuestionsExt(val); setDirty(true); scheduleAutoSave(); };
  const wrapNotes = (val) => { setNotesLibres(val); setDirty(true); scheduleAutoSave(); };

  const scheduleAutoSave = useCallback(() => {
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      saveRef.current?.();
    }, 3000);
  }, []);

  useEffect(() => () => clearTimeout(autoSaveTimer.current), []);

  const loadNotes = async () => {
    try {
      setLoadingNotes(true);
      const res = await axios.get(`${API_URL}/api/crm/notes?dossier_id=${dossier.id}`);
      if (res.status === 200) { const data = res.data; setNotes(data.notes || []); }
    } catch (e) { console.error(e); }
    finally { setLoadingNotes(false); }
  };

  const saveQuestionnaire = useCallback(async (silent = false) => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API_URL}/api/crm/dossiers/${dossier.id}/checklists`, {
          questionnaire_type: typesTravaux,
          questions_interieur: questionsInt,
          questions_exterieur: questionsExt,
          notes_inspection: notesLibres,
        });
      if (res.status !== 200) throw new Error();
      setDirty(false);
      if (!silent) toast.success('Questionnaire sauvegardé !');
      if (onUpdate) onUpdate();
    } catch {
      if (!silent) toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  }, [dossier.id, typesTravaux, questionsInt, questionsExt, notesLibres, onUpdate]);

  // Mettre à jour la ref après chaque recréation
  useEffect(() => { saveRef.current = () => saveQuestionnaire(true); }, [saveQuestionnaire]);

  const selectType = (type) => {
    const newType = typesTravaux === type ? null : type;
    setTypesTravaux(newType);
    setDirty(true);
    scheduleAutoSave();
  };

  const ajouterNote = async () => {
    if (!nouvelleNote.trim()) { toast.error('Veuillez entrer une note'); return; }
    try {
      const res = await axios.post(`${API_URL}/api/crm/notes`, { dossier_id: dossier.id, contenu: nouvelleNote, auteur: 'Utilisateur', type_note: typeNote });
      if (res.status !== 200) throw new Error();
      toast.success('Note ajoutée !');
      setNouvelleNote('');
      loadNotes();
      if (onUpdate) onUpdate();
    } catch { toast.error("Erreur lors de l'ajout de la note"); }
  };

  const supprimerNote = async (noteId) => {
    try {
      const res = await axios.delete(`${API_URL}/api/crm/notes/${noteId}`);
      if (res.status !== 200) throw new Error();
      loadNotes();
      if (onUpdate) onUpdate();
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  // Résumé global
  const totalInt = questionsInt.filter(q => q.reponse !== null).length;
  const totalExt = questionsExt.filter(q => q.reponse !== null).length;

  return (
    <div className="space-y-5">
      <Tabs defaultValue="questionnaire">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="questionnaire" className="flex items-center gap-2" data-testid="tab-questionnaire">
            <AlertCircle className="w-4 h-4" />
            Questionnaire
            {(totalInt + totalExt) > 0 && (
              <Badge className="ml-1 bg-blue-100 text-blue-700 text-xs">{totalInt + totalExt}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="notes" className="flex items-center gap-2" data-testid="tab-notes-libres">
            <MessageSquare className="w-4 h-4" />
            Notes ({notes.length})
          </TabsTrigger>
        </TabsList>

        {/* ── ONGLET QUESTIONNAIRE ─────────────────────────────────────── */}
        <TabsContent value="questionnaire" className="mt-4 space-y-4">

          {/* Sélection du type de travaux */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-gray-700">Type de travaux</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3 flex-wrap">
                {[
                  { key: 'interieur', label: 'Intérieur', icon: Home, color: 'border-blue-400 bg-blue-600 text-white', inactive: 'border-gray-200 text-gray-600 hover:border-blue-400 hover:text-blue-600' },
                  { key: 'exterieur', label: 'Extérieur', icon: TreePine, color: 'border-green-400 bg-green-600 text-white', inactive: 'border-gray-200 text-gray-600 hover:border-green-400 hover:text-green-600' },
                  { key: 'mixte', label: 'Mixte (Int. + Ext.)', icon: Layers, color: 'border-purple-400 bg-purple-600 text-white', inactive: 'border-gray-200 text-gray-600 hover:border-purple-400 hover:text-purple-600' },
                ].map(({ key, label, icon: Icon, color, inactive }) => (
                  <button
                    key={key}
                    type="button"
                    data-testid={`type-travaux-${key}`}
                    onClick={() => selectType(key)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 font-medium text-sm transition-all ${typesTravaux === key ? color : `bg-white ${inactive}`}`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </button>
                ))}
              </div>
              {!typesTravaux && (
                <p className="text-xs text-gray-400 mt-3">
                  Sélectionnez le type de travaux pour afficher les questions correspondantes.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Questions Intérieur */}
          {(typesTravaux === 'interieur' || typesTravaux === 'mixte') && (
            <Card className="border-blue-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold text-blue-700 flex items-center gap-2">
                  <Home className="w-4 h-4" />
                  Questions — Intérieur
                  <Badge className="bg-blue-100 text-blue-700 text-xs ml-auto">
                    {questionsInt.filter(q => q.reponse !== null).length}/{questionsInt.length}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <BlocQuestionnaire
                  type="interieur"
                  icon={Home}
                  title="Intérieur"
                  color="blue"
                  questions={questionsInt}
                  onChange={wrapQInt}
                />
              </CardContent>
            </Card>
          )}

          {/* Questions Extérieur */}
          {(typesTravaux === 'exterieur' || typesTravaux === 'mixte') && (
            <Card className="border-green-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold text-green-700 flex items-center gap-2">
                  <TreePine className="w-4 h-4" />
                  Questions — Extérieur
                  <Badge className="bg-green-100 text-green-700 text-xs ml-auto">
                    {questionsExt.filter(q => q.reponse !== null).length}/{questionsExt.length}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <BlocQuestionnaire
                  type="exterieur"
                  icon={TreePine}
                  title="Extérieur"
                  color="green"
                  questions={questionsExt}
                  onChange={wrapQExt}
                />
              </CardContent>
            </Card>
          )}

          {/* Notes libres d'inspection */}
          {typesTravaux && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold text-gray-700">Notes libres</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={notesLibres}
                  onChange={(e) => wrapNotes(e.target.value)}
                  placeholder="Observations générales, conditions particulières, informations complémentaires pour la représentante..."
                  rows={4}
                  data-testid="notes-libres-textarea"
                  className="resize-y min-h-[100px]"
                />
              </CardContent>
            </Card>
          )}

          {/* Bouton sauvegarde */}
          {typesTravaux && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-400">
                {dirty ? '● Non sauvegardé — sauvegarde auto dans quelques secondes' : '✓ Sauvegardé'}
              </p>
              <Button
                onClick={() => saveQuestionnaire(false)}
                disabled={saving}
                data-testid="save-questionnaire-btn"
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Sauvegarde...' : 'Sauvegarder'}
              </Button>
            </div>
          )}
        </TabsContent>

        {/* ── ONGLET NOTES LIBRES ──────────────────────────────────────── */}
        <TabsContent value="notes" className="mt-4 space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Ajouter une Note</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {['generale', 'suivi', 'rappel', 'importante'].map((type) => (
                  <Button
                    key={type}
                    variant={typeNote === type ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTypeNote(type)}
                    data-testid={`note-type-${type}`}
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </Button>
                ))}
              </div>
              <Textarea
                placeholder="Écrivez votre note ici..."
                value={nouvelleNote}
                onChange={(e) => setNouvelleNote(e.target.value)}
                rows={4}
                data-testid="nouvelle-note-textarea"
              />
              <Button onClick={ajouterNote} data-testid="ajouter-note-btn">
                <Plus className="w-4 h-4 mr-2" />
                Ajouter la Note
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Historique des Notes ({notes.length})</CardTitle></CardHeader>
            <CardContent>
              {loadingNotes ? (
                <p className="text-center text-gray-400 py-6">Chargement...</p>
              ) : notes.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Aucune note pour ce dossier</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {notes.map((note) => (
                    <Card key={note.id} className="hover:shadow-sm transition-shadow">
                      <CardContent className="pt-4">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center gap-2">
                            <Badge className={TYPE_COLORS[note.type_note] || TYPE_COLORS.generale}>
                              {note.type_note?.charAt(0).toUpperCase() + note.type_note?.slice(1)}
                            </Badge>
                            <span className="text-sm text-gray-500">
                              {new Date(note.date_creation).toLocaleDateString('fr-CA')} à{' '}
                              {new Date(note.date_creation).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          <Button variant="ghost" size="sm" data-testid={`supprimer-note-${note.id}`} onClick={() => supprimerNote(note.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                        <p className="text-gray-700 whitespace-pre-wrap text-sm">{note.contenu}</p>
                        <p className="text-xs text-gray-400 mt-2">Par {note.auteur}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
